import {
  useQuery,
  useMutation,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query'
import api from '@/consumer/lib/api'
import { scheduleResourceStateRefresh } from '@/shared/lib/schedule-resource-state-refresh'
import { finaliseSession } from '@/shared/lib/evidence/session-api'
import type { HoldingWarranty } from '@/consumer/hooks/use-holdings'
import type { EvidenceChecklistData } from '@/consumer/components/evidence-checklist'

// ── Types ────────────────────────────────────────────────

export interface WorkflowStateInfo {
  uuid: string
  key: string
  label: string
  color: string
  is_terminal: boolean
  is_initial: boolean
}

export interface ClaimHostAddress {
  display: string
  value?: Record<string, unknown>
}

export interface ClaimHostSummary {
  registration_number: string
  make: string
  // WPS-138 #3: present only for opted-in tenants with a property-shaped host;
  // null/absent otherwise, so the tile shows nothing.
  address?: ClaimHostAddress | null
}

export interface ConsumerClaimSummary {
  uuid: string
  product_name: string
  product_image_url: string | null
  organisation_name: string
  organisation_logo_url: string | null
  claim_schema_name: string
  claim_state: string | null
  claim_state_color: string | null
  is_terminal: boolean
  workflow_states: WorkflowStateInfo[]
  /** WPS-138 — claimant-facing status + stage tracker, matching the claim
   *  detail page. Populated only when the tenant has the cycle-time stage
   *  config; otherwise null and the card falls back to `claim_state` /
   *  `workflow_states`. Internal-only states collapse to "In Progress". */
  external_status_label?: string | null
  current_stage?: { key: string; label: string; helper: string } | null
  stages?: { key: string; label: string; order: number }[] | null
  awaiting_claimant?: boolean
  host: ClaimHostSummary | null
  has_pending_consumer_action: boolean
  /** The consumer has an unread staff-reply notification on this claim
   *  (server-side, per-user, cross-device). Drives the card attention dot. */
  has_unread_notification: boolean
  latest_customer_message_at?: string | null
  created_at: string | null
  /** WPS-141 — how this consumer reaches this claim: "owned" (their own
   *  holding), "shared" (external collaborator contact, can act on it), or
   *  "view_only" (external contact, read-only). Drives the shared/view-only
   *  badge on the claim card. */
  access_type: 'owned' | 'shared' | 'view_only'
}

export interface WorkflowInstance {
  claim_uuid: string
  workflow_uuid: string
  workflow_name: string
  current_state_uuid: string
  current_state_key: string
  current_state_label: string
  current_state_color: string
  current_state_editable_fields: string[] | null
  current_state_is_terminal: boolean
  /** WP1-67 claimant-facing stage view (flag-gated; present for OC). The
   *  portal renders these instead of the internal status set. */
  current_stage?: { key: string; label: string; helper: string } | null
  external_status_label?: string | null
  awaiting_claimant?: boolean
  stages?: { key: string; label: string; order: number }[]
}

export interface EmailSent {
  uuid: string
  recipient_email: string
  recipient_name: string
  recipient_kind: string
  subject: string
  template_slug: string
  status: string
  rendered_html: string
  created_at: string | null
}

export interface ActionLogStateRef {
  key: string
  label: string
  color: string
}

export interface ActionLogEntry {
  uuid: string
  action_key: string
  action_label: string
  action_icon: string | null
  to_state_label: string | null
  /** State this transition moved into / out of (null for non-transitions). */
  to_state?: ActionLogStateRef | null
  from_state?: ActionLogStateRef | null
  /** Transition result; only ``'OK'`` entries count as genuine state visits. */
  result?: string | null
  initiated_by: {
    type: 'USER' | 'SYSTEM'
    user: { uuid: string; email: string; name?: string } | null
  }
  parameters: Record<string, unknown> | null
  captured_values: Record<string, unknown> | null
  emails_sent?: EmailSent[]
  created_at: string
}

export interface ConsumerActionFieldMeta {
  code_name: string
  display_name: string
  field_type: string
  required: boolean
  configuration_data: Record<string, unknown>
}

export interface InfoRequest {
  reason: string
  requested: { value: string; label: string }[]
}

export interface ConsumerAction {
  key: string
  label: string
  icon: string | null
  actionable: boolean
  fields: ConsumerActionFieldMeta[]
  /** Present when this action is a response to an admin info request. */
  info_request?: InfoRequest | null
  /** WPS-138 #14 - when true, the confirm dialog must collect a required
   *  comment (e.g. why the homeowner is disputing) before the action runs. */
  require_comment?: boolean
  /** Prompt shown above the required comment box. */
  comment_prompt?: string
  /** captured_values key the comment is posted under (server-defined, so the
   *  SPA does not hardcode it). */
  comment_field?: string
}

/** WPS-138 #14 - one line of the settlement the homeowner is approving. */
export interface ConsumerSettlementLineItem {
  description: string
  /** Product name for a priced line (null when the line carries none). */
  product_name: string | null
  /** Product colour for a priced line (null when the line carries none). */
  color: string | null
  /** A voucher line (carries a voucher quantity); rendered as a quantity only. */
  is_voucher: boolean
  /** Installed quantity (decimal string). */
  quantity: string
  /** Per-unit price (decimal string). */
  unit_price: string
  /** Line total = billable quantity x unit price (decimal string). */
  extended_price: string
  /** Voucher quantity (decimal string) for a voucher line; null otherwise. */
  voucher_quantity: string | null
}

/** WPS-138 #14 - the settlement summary shown on the homeowner approval. */
export interface ConsumerSettlement {
  /** Total of the priced (non-voucher) lines - the cash being approved. */
  total: string
  line_items: ConsumerSettlementLineItem[]
}

export interface DocumentCategorySubcategory {
  key: string
  label: string
}

/** Tenant's configurable document-category taxonomy for evidence uploads. */
export interface DocumentCategory {
  key: string
  label: string
  sections?: string[]
  required?: boolean
  subcategories?: DocumentCategorySubcategory[]
}

/** WPS-143 — a product spec attribute value (PUBLIC, non-holding), e.g. colour,
 *  surfaced next to the claim header. Generic: whatever variant/product
 *  attributes a tenant marks visible flow through here. */
export interface ClaimProductAttribute {
  code_name: string
  display_name: string
  field_type: string
  attribute_level: string
  value: unknown
}

export interface ConsumerClaimDetailResponse {
  claim: {
    uuid: string
    /** WPS-143 — canonical human-facing reference (tenant claim_number when
     *  assigned, else CLM-<uuid8>). The SAME string the submission email and
     *  claims list show. Older payloads may omit it; fall back to the CLM slice. */
    reference?: string
    product_name: string
    product_image_url: string | null
    /** WPS-143 — product spec attributes (PUBLIC, non-holding) for the header,
     *  e.g. colour. Empty when the product has no visible spec attributes. */
    product_attributes?: ClaimProductAttribute[]
    claim_schema_name: string
    attribute_values: Record<string, unknown>
    host: ClaimHostSummary | null
    /** WPS-138 — the claim's holding warranty (for the Warranty card).
     *  Null when the holding has no active warranty. */
    warranty: HoldingWarranty | null
    created_at: string | null
    updated_at: string | null
  }
  metadata: {
    workflow_instance: WorkflowInstance | null
    workflow_states: WorkflowStateInfo[]
    editable_fields: string[]
    /** Per-attribute field metadata (field_type, options, etc.) for the
     *  consumer-visible claim attributes. Drives field-type-aware display
     *  formatting so file/date/select/money values render correctly. */
    attribute_field_metadata: ConsumerActionFieldMeta[]
    consumer_actions: ConsumerAction[]
    /** Tenant's document-category taxonomy for the evidence uploader. */
    document_categories: DocumentCategory[]
    /** URL to the typed evidence upload page (when an active upload session exists). */
    evidence_upload_url: string | null
    /** WPS-138 #6 — the inline, categorised evidence checklist ({token, slots})
     *  for opted-in tenants; null otherwise (falls back to the link-out above). */
    evidence_checklist: EvidenceChecklistData | null
    /** WPS-141 — this consumer's portal access level on this claim: "owner"
     *  (their holding), "collaborator" (external contact, full write access),
     *  or "view_only" (external contact, read-only). */
    access_level: 'owner' | 'collaborator' | 'view_only'
    /** Convenience flag - true only when access_level is "owner". */
    is_owner: boolean
    /** WPS-143 — opt-in simplified claim view (Tenant.settings, off by default).
     *  When true, the page renders the streamlined summary the claim submission
     *  email links to: claim reference heading first, product + colour, and the
     *  detail/evidence/warranty/actions cards WITHOUT the internal workflow
     *  stepper or the full activity timeline. Generic: any tenant can enable it. */
    simplified_portal_view?: boolean
    /** WPS-138 #11 - when true, drop the internal ROLE badge ("Admin"/"Staff"/
     *  "Dealer") next to staff names on the activity timeline and notes, so an
     *  internal user is shown by first name only. Gated server-side on the
     *  tenant's hide-staff-last-name stance; a tenant that has not opted in
     *  keeps the role badge. */
    hide_staff_role?: boolean
    /** WPS-138 #14 - the settlement the homeowner is approving (line items +
     *  total), attached only for opted-in tenants when the claim carries a
     *  settlement; null otherwise. */
    settlement?: ConsumerSettlement | null
  }
  history: ActionLogEntry[]
  organisation: {
    name: string
    logo_url: string | null
    support_email: string
    contact_email: string
  }
}

// ── Hooks ────────────────────────────────────────────────

export function useConsumerClaims() {
  return useQuery<ConsumerClaimSummary[]>({
    queryKey: ['consumer-claims'],
    queryFn: async () => {
      const res = await api.get('/claims/list/')
      // Endpoint paginates above the page-size threshold - normalise to array.
      const data = res.data
      if (Array.isArray(data)) return data
      if (data && Array.isArray(data.results)) return data.results
      return []
    },
    staleTime: 30_000,
  })
}

/** WPS-142 #7 (consumer side): the follow-up auto-advance a `submit_documents`
 *  triggers runs ASYNCHRONOUSLY (a Pulse workflow-action binding delivered via
 *  Cloud Tasks) and commits AFTER the action's response returns, so the mounted
 *  claim view (which never polls) keeps showing the pre-advance stage until a
 *  manual reload. Delegates to the shared bounded re-poll (see the admin
 *  `scheduleClaimStateRefresh`); on advance it refreshes the consumer claims
 *  list. */
export function scheduleConsumerClaimStateRefresh(
  qc: QueryClient,
  uuid: string,
  previousStateKey: string | null | undefined,
): void {
  scheduleResourceStateRefresh(qc, {
    detailQueryKey: ['consumer-claim', uuid],
    previousStateKey,
    selectStateKey: (data) =>
      (data as ConsumerClaimDetailResponse | undefined)?.metadata
        ?.workflow_instance?.current_state_key ?? null,
    onStateAdvanced: () =>
      qc.invalidateQueries({ queryKey: ['consumer-claims'] }),
  })
}

export function useConsumerClaimDetail(uuid: string | undefined) {
  return useQuery<ConsumerClaimDetailResponse>({
    queryKey: ['consumer-claim', uuid],
    queryFn: async () => {
      const res = await api.get(`/claims/${uuid}/`)
      return res.data
    },
    enabled: !!uuid,
    staleTime: 30_000,
  })
}

export function useConsumerClaimEdit() {
  const qc = useQueryClient()
  return useMutation<
    Record<string, unknown>,
    Error,
    { uuid: string; attribute_values: Record<string, unknown> }
  >({
    mutationFn: async ({ uuid, attribute_values }) => {
      const res = await api.put(`/claims/${uuid}/`, { attribute_values })
      return res.data
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['consumer-claim', vars.uuid] })
      qc.invalidateQueries({ queryKey: ['consumer-claims'] })
    },
  })
}

export function useConsumerClaimAddEvidence() {
  const qc = useQueryClient()
  return useMutation<
    Record<string, unknown>,
    Error,
    { uuid: string; file_uuid: string; category?: string; subcategory?: string }
  >({
    mutationFn: async ({ uuid, file_uuid, category, subcategory }) => {
      const res = await api.post(`/claims/${uuid}/add-evidence/`, {
        file_uuid,
        category,
        subcategory,
      })
      return res.data
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['consumer-claim', vars.uuid] })
      qc.invalidateQueries({ queryKey: ['consumer-claims'] })
    },
  })
}

/** Submit the claim's live evidence request: finalise its upload session. */
export function useSubmitClaimEvidence() {
  const qc = useQueryClient()
  return useMutation<
    void,
    Error,
    { claimUuid: string; token: string; previousStateKey?: string | null }
  >({
    mutationFn: ({ token }) => finaliseSession(token),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['consumer-claim', vars.claimUuid] })
      qc.invalidateQueries({ queryKey: ['consumer-claims'] })
      scheduleConsumerClaimStateRefresh(qc, vars.claimUuid, vars.previousStateKey)
    },
  })
}

export function useConsumerClaimContact() {
  return useMutation<
    { message: string },
    Error,
    { uuid: string; message: string }
  >({
    mutationFn: async ({ uuid, message }) => {
      const res = await api.post(`/claims/${uuid}/contact/`, { message })
      return res.data
    },
  })
}

export function useConsumerClaimExecuteAction() {
  const qc = useQueryClient()
  return useMutation<
    Record<string, unknown>,
    Error,
    { uuid: string; action_key: string; captured_values: Record<string, unknown> }
  >({
    mutationFn: async ({ uuid, action_key, captured_values }) => {
      const res = await api.post(`/claims/${uuid}/execute-action/`, {
        action_key,
        captured_values,
      })
      return res.data
    },
    onSuccess: (_, vars) => {
      const previousStateKey =
        qc.getQueryData<ConsumerClaimDetailResponse>(['consumer-claim', vars.uuid])
          ?.metadata?.workflow_instance?.current_state_key ?? null
      qc.invalidateQueries({ queryKey: ['consumer-claim', vars.uuid] })
      qc.invalidateQueries({ queryKey: ['consumer-claims'] })
      // WPS-142 #7: the action's follow-up auto-advance may commit asynchronously
      // after this response; re-poll the detail until the stage changes so the
      // portal reflects it without a manual reload.
      scheduleConsumerClaimStateRefresh(qc, vars.uuid, previousStateKey)
    },
  })
}
